/**
 * Utility functions for base64 encoding and decoding with full Unicode support.
 * The standard btoa() and atob() functions do not support characters outside the Latin1 range.
 * Characters outside that range (i.e. characters that would use more than one byte in UTF-8,
 * including some accented latin characters, all the other alphabets, emojis, etc.) cause an error.
 * Here we use TextEncoder and TextDecoder to handle full Unicode strings (internal representation of strings
 * in Javascript is UTF-16).
 *
 * In the future we might be able to use Uint8Array.prototype.toBase64() and Uint8Array.fromBase64()
 * but this proposal is still in stage 3 and not supported by all browsers.
 * See: https://tc39.es/proposal-arraybuffer-base64/spec/#sec-uint8array.prototype.tobase64
 */

// Re-use the TextEncoder and TextDecoder instances
const textEncoder = new TextEncoder(); // Default encoding is UTF-8
const textDecoder = new TextDecoder();

/**
 * Encodes a Unicode string to UTF-8 + Base64.
 *
 * The function first encodes the text to UTF-8 bytes, then back to a string consisting of only single-byte chars,
 * and finally to Base64 using btoa().
 *
 * See: https://developer.mozilla.org/en-US/docs/Web/API/Window/btoa
 * See: See https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_unicode_problem.
 *
 * @param text - The text to encode
 * @returns The string encoded ast UTF-8 and then Base64
 */
export function encodeBase64(text: string): string {
  // encode to bytes  to UTF-8
  const bytes = textEncoder.encode(text);

  // convert to single-byte chars (using for loop for performance)
  const chars: string[] = [];
  for (let i = 0; i < bytes.length; i++) {
    chars[i] = String.fromCodePoint(bytes[i]);
  }

  // join chars
  const binaryString = chars.join('');

  // encode to base64
  return btoa(binaryString);
}

/**
 * Decodes a UTF-8 string encoded as Base64.
 *
 * The function first decodes the Base64 string to a string consisting of only single-byte chars,
 * then converts the string to a byte arrray an finally decodes the byte array using UTF-8.
 *
 * @param base64 - The base64 encoded string to decode
 * @returns The decoded text
 */
export function decodeBase64(base64: string): string {
  // decode from base64 to single-byte chars string
  const binaryString = atob(base64);

  // convert to bytes (using for loop for performance)
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // decode from UTF-8
  return textDecoder.decode(bytes);
}
